<template>
    <div>
        <SmartBreadcrumbs
            type="a"
            :pages="[
                {
                    name: $tc('messages.grades', 2),
                    href: null,
                },
            ]"
        >
            <template v-slot:corner>
                <ActivityMenuOptions>
                    <GroupSelector
                        :grade-levels="meta.links.gradeLevels"
                        :user-grade-levels="meta.info.user_grade_levels"
                    >
                        <template v-slot:default="{ filteredGradeLevels }">
                            <div class="mb-2" v-for="gradeLevel in filteredGradeLevels" :key="gradeLevel.id">
                                <AppButton
                                    element="inertia-link"
                                    v-for="group in gradeLevel.groups"
                                    :href="group.url"
                                    :key="group.id"
                                    desired-action
                                    class="mr-2"
                                >
                                    {{ group.name }}
                                </AppButton>
                            </div>
                        </template>
                    </GroupSelector>
                </ActivityMenuOptions>
            </template>
        </SmartBreadcrumbs>
        <div>
            <form :action="meta.links.show" method="GET" @submit.prevent="filter" class="p-4" id="trimester_grades">
                <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 md:grid-cols-12">
                    <div class="sm:col-span-6">
                        <div class="mt-1">
                            <FormLabel :value="$tc('messages.domains', 2)" for="category_ids" />
                            <SlideOverCheckboxInput v-model="form.category_ids" :options="options.categories">
                                <div class="flex justify-end">
                                    <div class="mt-1">
                                        <SubmitFormButton
                                            form="trimester_grades"
                                            :button-text="$t('messages.filter')"
                                            :processing="form.processing"
                                            is-filter
                                        />
                                    </div>
                                </div>
                            </SlideOverCheckboxInput>
                            <FormError :value="form.errors.category_ids" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.trimester')" for="trimester" />
                            <FormSelect
                                required
                                id="trimester"
                                v-model="form.trimester"
                                :options="options.trimesters"
                                class="mt-1 w-full"
                                :null-option="false"
                            />
                            <FormError :value="form.errors.trimester" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2" v-show="options.school_years.length > 1">
                        <div class="mt-1">
                            <FormLabel :value="$t('messages.school_year')" for="school_year_id" />
                            <FormSelect
                                required
                                id="school_year_id"
                                v-model="form.school_year_id"
                                :options="options.school_years"
                                class="mt-1 w-full"
                                :null-option="false"
                            />
                            <FormError :value="form.errors.school_year_id" class="mt-2" />
                        </div>
                    </div>

                    <div class="sm:col-span-2">
                        <label class="block text-sm font-medium leading-5 text-gray-700"> &nbsp; </label>
                        <div class="mt-1">
                            <SubmitFormButton
                                :button-text="$t('messages.filter')"
                                :processing="form.processing"
                                is-filter
                            />
                        </div>
                    </div>
                </div>
            </form>
        </div>
        <div v-if="filters.trimester === 'all'"></div>
        <div class="flex justify-end" v-else-if="meta.is_locked">
            <div>
                <AlertMessage class="mt-5 inline-block">
                    <template v-slot:title>
                        {{ $t('messages.warning') }}
                    </template>
                    <template v-slot:body>
                        <div>
                            {{ $t('messages.grading_period_closed') }}

                            <InertiaSquareDeleteButton
                                :href="meta.links.grading_period_destroy"
                                :confirm-text="$t('messages.are_you_sure?')"
                                v-if="meta.can.edit_grading_period"
                            />
                        </div>

                        <div class="mt-1 flex gap-x-2 max-w-xs whitespace-normal">
                            <span>
                                <IconAlertTriangle
                                    class="h-6 w-6"
                                    :class="meta.info.reportStatus.is_visible ? 'text-yellow-400' : 'text-gray-300'"
                                />
                            </span>
                            <p class="text-xs">{{ meta.info.reportStatus.explanation }}</p>
                        </div>
                    </template>
                </AlertMessage>
            </div>
        </div>
        <div v-else class="flex gap-x-2">
            <div v-if="meta.can.edit_grading_period">
                <InertiaSquareConfirmActionButton
                    :href="meta.links.grading_period_store"
                    danger
                    :title="$t('messages.are_you_sure?')"
                    :confirm-text="$t('messages.lock')"
                    method="post"
                >
                    <template v-slot:icon>
                        <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                            <path
                                fill-rule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clip-rule="evenodd"
                            ></path>
                        </svg>
                    </template>

                    {{
                        $t('messages.grading_period_close', {
                            group: meta.info.group.name,
                            trimester: meta.info.trimester,
                        })
                    }}
                </InertiaSquareConfirmActionButton>
            </div>
            <div class="mt-1 flex gap-x-2 max-w-xs whitespace-normal">
                <span>
                    <IconAlertTriangle
                        class="h-6 w-6"
                        :class="meta.info.reportStatus.is_visible ? 'text-yellow-400' : 'text-gray-300'"
                    />
                </span>
                <p class="text-xs">{{ meta.info.reportStatus.explanation }}</p>
            </div>
        </div>

        <TrimesterGradesTable
            :students="students"
            :categories="categories"
            :acc="acc"
            :can-edit="meta.can.edit"
            :is-locked="meta.is_locked"
            :ai-enabled="meta.info.has_ai_key"
        />
    </div>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import TrimesterGradesTable from '@/components/TrimesterGradesTable.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import FormError from '@/components/Forms/FormError.vue';
import SlideOverCheckboxInput from '@/components/Forms/SlideOverCheckboxInput.vue';
import AppButton from '@/components/AppButton.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import InertiaSquareDeleteButton from '@/components/InertiaSquareDeleteButton.vue';
import InertiaSquareConfirmActionButton from '@/components/InertiaSquareConfirmActionButton.vue';
import { IconAlertTriangle } from '@tabler/icons-vue';
import ActivityMenuOptions from '@/components/pageSpecific/ActivityMenuOptions.vue';
import GroupSelector from '@/components/GroupSelector.vue';
import SubmitFormButton from '@/Pages/Teacher/Activities/Subactivities/SubmitFormButton.vue';

export default {
    components: {
        SubmitFormButton,
        GroupSelector,
        ActivityMenuOptions,
        TrimesterGradesTable,
        FormLabel,
        FormSelect,
        FormError,
        SlideOverCheckboxInput,
        AppButton,
        AlertMessage,
        InertiaSquareDeleteButton,
        InertiaSquareConfirmActionButton,
        IconAlertTriangle,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'students', 'categories', 'acc', 'options', 'filters'],
    data() {
        return {
            form: this.$inertia.form({
                trimester: this.filters.trimester,
                category_ids: this.filters.category_ids,
                school_year_id: this.filters.school_year_id,
            }),
        };
    },
    methods: {
        filter() {
            this.form.get(this.meta.links.show);
        },
    },
};
</script>
